import React, { useRef, useState } from "react";
import "./contact.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";
import Popup from "./Popup";
import github from "../../img/github.svg";
import linkedin from "../../img/linkedin.svg";

const CONTACT_EMAIL = "dev.mohamed.ali1@gmail.com";

// Submissions are handled by Netlify Forms (no server or API keys needed).
// The matching static form that Netlify detects at deploy time lives in
// public/contact-form.html. Keep the field names in both files in sync.
const encode = (data) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");

const Contact = () => {
  const form = useRef();
  const [showPopup, setShowPopup] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const sendEmail = async (e) => {
    e.preventDefault();

    setIsSending(true);
    try {
      const formData = Object.fromEntries(new FormData(form.current));
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(formData),
      });
      if (!response.ok) {
        throw new Error(`Form submission failed with status ${response.status}`);
      }
      setShowPopup(true);
      form.current.reset();
      toast.success(
        "Message sent! Thanks for reaching out — you should hear back shortly.",
      );
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(
        "Something went wrong sending that. Please try again or email me directly.",
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="contact-form">
      <motion.div
        className="c-left"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.7, type: "spring" }}
      >
        <div className="contact-heading">
          <span className="section-eyebrow">Get in Touch</span>
          <h2 className="contactMe">Contact Me</h2>
          <p className="contactIntro">
            Have a project in mind or just want to say hello? Send a message and
            I&apos;ll get back to you within 24 hours.
          </p>
        </div>

        <div className="contact-details">
          <a
            className="contact-detail"
            href={`mailto:${CONTACT_EMAIL}`}
            onClick={() => {
              if (navigator.clipboard) {
                navigator.clipboard
                  .writeText(CONTACT_EMAIL)
                  .then(() => toast.info(`Email copied: ${CONTACT_EMAIL}`))
                  .catch(() => {});
              }
            }}
          >
            <Icon icon="mdi:email-outline" />
            <span>{CONTACT_EMAIL}</span>
          </a>
          <div className="contact-detail">
            <Icon icon="mdi:map-marker-outline" />
            <span>Westminster, London</span>
          </div>
          <div className="contact-detail">
            <Icon icon="mdi:clock-outline" />
            <span>Usually responds within 24 hours</span>
          </div>
        </div>

        <div className="contact-socials">
          <a
            href="https://github.com/devMoAli"
            aria-label="GitHub"
            target="_blank"
            rel="noreferrer"
          >
            <img src={github} alt="GitHub" />
          </a>
          <a
            href="https://www.linkedin.com/in/dev-mohamed-ali/"
            aria-label="LinkedIn"
            target="_blank"
            rel="noreferrer"
          >
            <img src={linkedin} alt="LinkedIn" />
          </a>
        </div>
      </motion.div>

      <div className="c-right">
        {showPopup && (
          <Popup
            message="Thanks for reaching out — I'll get back to you soon."
            onClose={() => setShowPopup(false)}
          />
        )}
        <form
          ref={form}
          name="contact"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          onSubmit={sendEmail}
        >
          <input type="hidden" name="form-name" value="contact" />
          <p style={{ display: "none" }} aria-hidden="true">
            <label>
              Leave this empty: <input name="bot-field" tabIndex="-1" autoComplete="off" />
            </label>
          </p>
          <div className="field">
            <label htmlFor="user_name">Name</label>
            <input
              id="user_name"
              type="text"
              name="user_name"
              className="user"
              placeholder="Jane Doe"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="user_email">Email</label>
            <input
              id="user_email"
              type="email"
              name="user_email"
              className="user"
              placeholder="jane@company.com"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="subject">Subject</label>
            <input
              id="subject"
              type="text"
              name="subject"
              className="user"
              placeholder="Project inquiry"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              className="user"
              placeholder="Tell me a bit about what you need..."
              required
            />
          </div>
          <div className="submitBtn">
            <button type="submit" className="button" disabled={isSending}>
              {isSending ? "Sending…" : "Send"}
              {!isSending && (
                <Icon icon="mdi:send-outline" width="1rem" height="1rem" />
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Contact;
